import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const pullToRefresh = usePullToRefresh({
    onRefresh: async () => {
      navigate("/");
    },
  });

  return (
    <div
      ref={pullToRefresh.containerRef}
      className="min-h-screen flex flex-col bg-background overflow-auto"
    >
      <PullToRefreshIndicator
        isVisible={pullToRefresh.shouldShowIndicator}
        isRefreshing={pullToRefresh.isRefreshing}
        pullDistance={pullToRefresh.pullDistance}
      />

      <Header title="Privacy Policy" showBackButton />

      <div className="flex-1 p-6 max-w-4xl mx-auto w-full">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

          <p className="text-muted-foreground mb-4">
            <strong>Last updated:</strong> {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>

          {/* Operational disclosure — required by Play Store Misleading Claims policy */}
          <div className="rounded-lg border border-border bg-muted/50 px-5 py-4 mb-8">
            <h2 className="text-lg font-semibold mb-2">About This App</h2>
            <p className="text-sm text-muted-foreground mb-2">
              Mining Matters is developed by <strong>Develop Digitally Limited</strong> on behalf of the{" "}
              <strong>Mines and Geology Division (MGD)</strong>, Ministry of Agriculture,
              Fisheries and Mining. Grievances submitted through this app are reviewed and
              responded to by MGD.
            </p>
            <p className="text-sm text-muted-foreground">
              Official information: {" "}
              <a
                href="https://mgd.gov.jm"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium underline underline-offset-2"
              >
                mgd.gov.jm
              </a>
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p className="mb-4">
              Mining Matters ("we", "our", or "us") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our mobile application and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">Personal Information</h3>
            <p className="mb-4">
              When you submit a grievance case through our app, we may collect:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Your name and contact information</li>
              <li>Location data (parish, coordinates if provided)</li>
              <li>Case details and descriptions</li>
              <li>Photos and media files you upload</li>
              <li>Device information for push notifications</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">Automatically Collected Information</h3>
            <p className="mb-4">
              We may automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Device type and operating system</li>
              <li>Unique device identifiers</li>
              <li>IP address</li>
              <li>App usage data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Process and manage grievance cases</li>
              <li>Communicate with you about your cases</li>
              <li>Send push notifications about case updates</li>
              <li>Improve our services and user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Storage and Security</h2>
            <p className="mb-4">
              Your data is stored securely on our servers. We implement appropriate technical
              and organizational measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction.
            </p>
            <p className="mb-4">
              However, no method of transmission over the Internet or electronic storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Data Sharing</h2>
            <p className="mb-4">
              We do not sell your personal information. We may share your information only in
              the following circumstances:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>With service providers who assist in operating our services (under confidentiality agreements)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Push Notifications</h2>
            <p className="mb-4">
              We use push notifications to inform you about updates to your grievance cases.
              You can manage notification preferences through your device settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt out of certain data collection practices</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p className="mb-4">
              To exercise these rights, please contact us using the contact information provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
            <p className="mb-4">
              Our services are not intended for children under the age of 13. We do not knowingly
              collect personal information from children under 13. If you believe we have collected
              information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
              You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mb-4">
              <strong>Email:</strong> [Your contact email]<br />
              <strong>Organization:</strong> Mining Matters<br />
              <strong>Address:</strong> [Your organization address]
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
